import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { WhatsappSession, WhatsappMonitoredChat } from "@/lib/types";
import { useRealtimeFallback } from "@/lib/hooks/useConnections";

const sessionKey = ["whatsapp-session"] as const;
const chatsKey = ["whatsapp-chats"] as const;

// ---------------------------------------------------------------------------
// Sessao WhatsApp do tenant (status do pareamento via Evolution API)
// ---------------------------------------------------------------------------

async function fetchWhatsappSession(): Promise<WhatsappSession | null> {
  const { data, error } = await supabase
    .from("whatsapp_sessions")
    .select("id, tenant_id, phone, status, revoked_reason, last_health_check_at, last_connected_at, created_at")
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return (data ?? null) as WhatsappSession | null;
}

export function useWhatsappSession() {
  const fallback = useRealtimeFallback();
  return useQuery<WhatsappSession | null, Error>({
    queryKey: sessionKey,
    queryFn: fetchWhatsappSession,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    refetchInterval: () => fallback(),
  });
}

// ---------------------------------------------------------------------------
// Chats monitorados (origem) e destinos do canal WhatsApp
// ---------------------------------------------------------------------------

async function fetchWhatsappChats(): Promise<WhatsappMonitoredChat[]> {
  const { data, error } = await supabase
    .from("whatsapp_monitored_chats")
    .select("id, tenant_id, jid, title, role, status, created_at")
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data ?? []) as WhatsappMonitoredChat[];
}

export function useWhatsappChats() {
  return useQuery<WhatsappMonitoredChat[], Error>({
    queryKey: chatsKey,
    queryFn: fetchWhatsappChats,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

async function removeWhatsappChat(id: string): Promise<void> {
  const { error } = await supabase.from("whatsapp_monitored_chats").delete().eq("id", id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Desconectar sessao WhatsApp
// ---------------------------------------------------------------------------

// Revoga na hora e pede ao bot para deslogar a instancia na Evolution
// (RPC disconnect_connection). UPDATE direto deixaria a instancia viva.
async function disconnectWhatsappSession(): Promise<void> {
  const { error } = await supabase.rpc("disconnect_connection", { p_channel: "whatsapp" });
  if (error) throw error;
}

export function useDisconnectWhatsapp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: disconnectWhatsappSession,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sessionKey });
      await queryClient.invalidateQueries({ queryKey: ["connection-session", "whatsapp"] });
    },
  });
}

export function useRemoveWhatsappChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeWhatsappChat,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: chatsKey });
    },
  });
}
